/**
 * Reactions are kept apart from natural behaviors (useAvatarFaceBehavior):
 * they're intentional responses to game events like wins, losses, or surprises.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useAvatarRAF } from './useAvatarRAF';
import {
  type ReactionType,
  calculateReactionTransform,
  isValidReactionType,
} from '../configs/reactions';

/**
 * Props for useAvatarReactions hook
 */
interface UseAvatarReactionsProps {
  isReacting?: boolean; // Whether reaction is currently active
  reactionType?: ReactionType; // Type of reaction to perform
}

/**
 * Head transform values
 */
interface HeadTransform {
  x: number;
  y: number;
  rotation: number;
  scale: number;
}

/**
 * Return type for useAvatarReactions hook
 */
interface UseAvatarReactionsReturn {
  headTransform: HeadTransform; // Head animation transform values
  isReactionActive: boolean; // Whether reaction animation is currently active
}

/**
 * Default/neutral transforms
 */
const NEUTRAL_HEAD_TRANSFORM: HeadTransform = {
  x: 0,
  y: 0,
  rotation: 0,
  scale: 1,
};

/**
 * Hook for avatar game-triggered reactions
 *
 * Manages emotional reactions that avatars perform in response to game events.
 * Uses the reactions configuration system for consistent, configurable animations.
 *
 * @param props - Configuration for reaction behavior
 * @returns Current transform values and reaction state
 */
export function useAvatarReactions({
  isReacting = false,
  reactionType,
}: UseAvatarReactionsProps = {}): UseAvatarReactionsReturn {
  const { subscribe } = useAvatarRAF();

  // Animation transform state
  const [headTransform, setHeadTransform] = useState<HeadTransform>(
    NEUTRAL_HEAD_TRANSFORM
  );
  const [isReactionActive, setIsReactionActive] = useState(false);

  // Internal animation timing (refs to avoid re-renders)
  const animationTimeRef = useRef(0);

  /**
   * Update reaction animation transforms
   */
  const updateReactionAnimation = useCallback(
    (deltaTime: number) => {
      if (!isReacting || !reactionType || !isValidReactionType(reactionType)) {
        return;
      }

      // Update animation time
      animationTimeRef.current += deltaTime * 0.001; // Convert to seconds
      const time = animationTimeRef.current;

      // Calculate head transform using reaction configuration
      const transforms = calculateReactionTransform(reactionType, time);
      setHeadTransform(transforms.head);
    },
    [isReacting, reactionType]
  );

  // Handle reaction start/stop
  useEffect(() => {
    if (isReacting && reactionType && isValidReactionType(reactionType)) {
      // Start reaction
      setIsReactionActive(true);
      animationTimeRef.current = 0;
    } else {
      // Stop reaction - reset to neutral transform
      setIsReactionActive(false);
      setHeadTransform(NEUTRAL_HEAD_TRANSFORM);
      animationTimeRef.current = 0;
    }
  }, [isReacting, reactionType]);

  // Subscribe to RAF for reaction animation updates
  useEffect(() => {
    if (!isReacting || !reactionType) {
      return;
    }

    return subscribe((deltaTime: number) => {
      updateReactionAnimation(deltaTime);
    });
  }, [subscribe, isReacting, reactionType, updateReactionAnimation]);

  return {
    headTransform,
    isReactionActive,
  };
}
